import './Codi.css';
import './DesignSystem.css';
import { useMemo, useState } from 'react';
import { resolvedPosition, useWindows } from './Windows';
import { TiArrowRight } from 'react-icons/ti';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  Column,
  ErrorMessage,
  Font,
  Row,
  Scrollable,
  SecondaryButton,
} from './DesignSystem';
import {
  useAllItems,
  useConversation,
  UserMessage,
  type ConversationMessage,
} from '@/services/ConversationService';
import { useVersions } from './VersionsPreview';
import { MarkdownColorComponents } from './CodiColorTokens';
import { EnableCleanSlate, EnableDevJSONImport } from './CleanSlate';
import { Palette, PaletteMetrics } from './Palette';
import { Spacer } from './ColorPreview';

const EmojiRegex = /[\p{Extended_Pictographic}\uFE0F]/gu;

function stripEmojis(text: string): string {
  return text.replace(EmojiRegex, '');
}

const PromptWidth = 380;
const SubmitDiameter = 36;
const MessageInlineMargin = 12;
const JsonImportPaletteY = 200; // Position below header, within initial viewport

type QuestTaskStatus = 'none' | 'in-progress' | 'completed';
type QuestTask = {
  name: string;
  status: QuestTaskStatus;
};
type Quest = {
  title: string;
  tasks: QuestTask[];
};

function QuestComponent({ quest }: { quest: Quest }) {
  return (
    <div style={{ padding: 4, borderRadius: 4, backgroundColor: 'white' }}>
      <div>{quest.title}</div>
      {quest.tasks.map((task) => {
        return <div key={task.name}>{task.name}</div>;
      })}
    </div>
  );
}

function ChatMessage({
  message,
  isLoading,
}: {
  message: ConversationMessage;
  isLoading?: boolean;
}) {
  const messageClass: string = (() => {
    switch (message.sender) {
      case 'user':
        return 'user-message';
      case 'system':
        if (isLoading) {
          return 'loader';
        }
        return 'thin-border';
    }
  })();

  function MessageBody() {
    const messageContent = stripEmojis(message.content);

    if (message.sender === 'user') {
      return <>{messageContent}</>;
    }

    return (
      <ReactMarkdown
        components={MarkdownColorComponents}
        remarkPlugins={[remarkGfm]}
      >
        {messageContent}
      </ReactMarkdown>
    );
  }

  return (
    <div
      className={`codi-text ${messageClass}`}
      style={{
        padding: 10,
        paddingLeft: message.sender === 'system' ? 18 : 10,
        marginInlineStart: message.sender === 'user' ? MessageInlineMargin : 0,
        marginInlineEnd: message.sender === 'system' ? MessageInlineMargin : 0,
        backgroundSize: isLoading ? '300% 300%' : '100% 100%',
        backgroundColor: isLoading ? 'transparent' : undefined,
        backgroundImage: isLoading
          ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.8), rgba(165, 212, 214, 0.5))'
          : undefined,
        animation: isLoading ? 'gradientFlow 5s ease infinite' : 'none',
      }}
    >
      <MessageBody />
    </div>
  );
}

function ChatHistory({ messages }: { messages: ConversationMessage[] }) {
  const { state: conversationState } = useConversation();

  return (
    <div style={{ marginBlockEnd: 8 }}>
      {messages.map((message, i) => {
        return (
          <div
            key={i}
            style={{ marginBlockEnd: i + 1 < messages.length ? 8 : 0 }}
          >
            <ChatMessage message={message} />
          </div>
        );
      })}
      {conversationState.loading !== undefined && (
        <div
          style={{
            marginBlockStart: 8,
          }}
        >
          <ChatMessage
            message={{ content: 'Codifying...', sender: 'system' }}
            isLoading={true}
          />
        </div>
      )}
    </div>
  );
}

export function Codi() {
  type CodiMode = 'collapsed' | 'expanded';
  const [mode, setMode] = useState<CodiMode>('expanded');
  const [prompt, setPrompt] = useState<string>('');
  const { service: conversationService, state: conversationState } =
    useConversation();
  const {
    state: { activeVersionID },
  } = useVersions();

  const allItems = useAllItems({ activeVersionID });
  const messages = useMemo(() => {
    return allItems.reduce<ConversationMessage[]>((acc, e) => {
      if (e.type !== 'message') {
        return acc;
      }

      return acc.concat({ content: e.content, sender: e.sender });
    }, []);
  }, [allItems]);
  const { state: windowState, dispatch: windowDispatch } = useWindows();

  const position = useMemo(() => {
    return resolvedPosition('codi', windowState);
  }, [windowState]);

  const submit = () => {
    if (prompt === '') {
      return;
    }

    if (conversationState.loading !== undefined) {
      return;
    }

    setPrompt('');

    conversationService.sendItems({
      newItems: [UserMessage({ content: prompt })],
      stream: EnableCleanSlate,
    });

    setMode('expanded');
  };

  const retry = () => {
    if (
      conversationState.loading !== undefined ||
      conversationState.draftItems.length === 0
    ) {
      return;
    }

    conversationService.sendItems({
      newItems: [],
      stream: EnableCleanSlate,
    });
    setMode('expanded');
  };

  const sizeToFit = (textarea: HTMLTextAreaElement) => {
    textarea.style.height = 'inherit';
    textarea.style.height = `${textarea.scrollHeight}px`;
  };

  if (windowState.windows.codi.isHidden === true || position === null) {
    return <></>;
  }

  const height = (() => {
    if (windowState.windows.codi.size?.height === undefined) {
      return 'auto';
    }
    return windowState.windows.codi.size.height - 16;
  })();

  const noItems = allItems.length === 0;
  const hasStartedConversation = conversationState.type === 'conversation';
  const isPromptEmpty = prompt.trim().length === 0;

  return (
    <Palette
      title="Codi Chat"
      windowID="codi"
      headless={noItems}
      trailingAction={
        hasStartedConversation && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
            }}
          >
            <SecondaryButton
              text="Restart Chat"
              onClick={() => {
                conversationService.startNewConversation();
                setPrompt('');
              }}
              isDisabled={conversationState.loading !== undefined}
            />
          </div>
        )
      }
    >
      {mode === 'expanded' && (
        <Scrollable
          style={{
            flexGrow: 1,
            minHeight: 0,
            paddingInlineEnd: 4,
          }}
        >
          <ChatHistory messages={messages} />
        </Scrollable>
      )}

      <form style={{ width: '100%', justifyContent: 'flex-end', flexGrow: 0 }}>
        <Column style={{ alignItems: 'center', width: '100%' }}>
          {conversationState.errorMessage !== null && (
            <Column
              role="alert"
              style={{
                marginInline: 8,
                marginBlockStart: 8,
                textAlign: 'center',
                alignItems: 'center',
              }}
            >
              <ErrorMessage message={conversationState.errorMessage} />

              <SecondaryButton
                text="Retry"
                onClick={retry}
                isDisabled={
                  conversationState.loading !== undefined ||
                  conversationState.draftItems.length === 0
                }
              />
            </Column>
          )}
          <textarea
            className="default-text prompt-input input"
            style={{
              flexGrow: 2,
              width: 'calc(100% - 20px)',
              backgroundColor: 'white',
              resize: 'inherit',
              marginInline: 8,
              marginBlock: 8,
              paddingInlineStart: 10,
              paddingBlockStart: 10,
            }}
            value={prompt}
            onChange={(e) => {
              const textarea = e.target;
              setPrompt(textarea.value);
              sizeToFit(textarea);
            }}
            onKeyUp={(e) => {
              e.preventDefault();
              if (e.key !== 'Enter' || e.shiftKey) {
                return;
              }
              submit();
            }}
            placeholder="Enter Prompt"
          />

          <Row style={{ alignItems: 'center', width: '100%' }}>
            <Spacer />
            <button
              type="submit"
              disabled={isPromptEmpty}
              className="submit-button"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              onClick={(e) => {
                submit();
                e.preventDefault();
              }}
            >
              <TiArrowRight size={36} color="white" />
            </button>
          </Row>
        </Column>
      </form>

      {EnableDevJSONImport && conversationState.type === 'new' && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            marginBlockStart: 12,
          }}
        >
          <Font kind="font-body">
            <button
              type="button"
              style={{
                cursor: 'pointer',
                textDecoration: 'underline',
                background: 'none',
                border: 'none',
                padding: 0,
                font: 'inherit',
                color: 'inherit',
              }}
              onClick={() => {
                windowDispatch({
                  type: 'setPosition',
                  id: 'json-import',
                  position: {
                    type: 'relative',
                    x: 'center',
                    y: JsonImportPaletteY,
                  },
                });
                windowDispatch({ type: 'show', id: 'json-import' });
              }}
            >
              or preview from JSON
            </button>
          </Font>
        </div>
      )}
    </Palette>
  );
}
